import { useRef, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCustomPages } from '@/hooks/useCustomPages';
import { slugify, type MerchantCustomPage, type MerchantCustomPageInput } from '@/lib/customPages';
import { buildSubdomain } from '@/lib/domain';
import { getCommerceId } from '@/lib/ownership';
import { supabase } from '@/lib/supabase';
import { uploadMediaFile } from '@/hooks/useUpload';

const EMPTY_FORM: MerchantCustomPageInput = {
  title: '',
  slug: '',
  content: '',
  published: true,
};

const TAG_HINTS = [
  '{{site_name}}', '{{site_description}}', '{{site_phone}}', '{{site_email}}',
  '{{products}}', '{{services}}', '{{hebergements}}', '{{contact_form}}',
  '{{testimonials}}', '{{faq}}', '{{gallery}}', '{{year}}',
];

export default function CustomPagesPage() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);
  const { pages, loading, error, create, update, remove } = useCustomPages(merchantId);

  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState<MerchantCustomPage | null>(null);
  const [form, setForm] = useState<MerchantCustomPageInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // Design -> page par IA
  const [designUrl, setDesignUrl] = useState('');
  const [designPreview, setDesignPreview] = useState('');
  const [designUploading, setDesignUploading] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const designInputRef = useRef<HTMLInputElement>(null);

  const siteBase = useMemo(
    () => (user?.user_name ? buildSubdomain(user.user_name).replace(/\/+$/, '') : ''),
    [user],
  );

  const publicUrl = (slug: string) => `${siteBase}/p/${slug}`;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setDesignUrl('');
    setDesignPreview('');
    setAiInstructions('');
    setAiError('');
    setShowEditor(true);
  };

  const openEdit = (page: MerchantCustomPage) => {
    setEditing(page);
    setForm({ title: page.title, slug: page.slug, content: page.content, published: page.published });
    setFormError('');
    setDesignUrl('');
    setDesignPreview('');
    setAiInstructions('');
    setAiError('');
    setShowEditor(true);
  };

  const handleDesignFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setAiError('Veuillez sélectionner une image (JPG, PNG, WebP).');
      return;
    }
    setAiError('');
    setDesignPreview(URL.createObjectURL(file));
    setDesignUploading(true);
    try {
      const url = await uploadMediaFile(file, 'designs');
      setDesignUrl(url);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "Erreur lors de l'upload du design");
    } finally {
      setDesignUploading(false);
    }
  };

  const handleGenerateFromDesign = async () => {
    if (!designUrl && !designPreview) {
      setAiError('Téléversez d\'abord un design ou un dessin.');
      return;
    }
    setAiError('');
    setAiGenerating(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('design-to-page', {
        body: { imageUrl: designUrl, instructions: aiInstructions, pageTitle: form.title },
      });
      if (fnErr) throw fnErr;
      const payload = data as { html?: string; error?: string } | null;
      if (payload?.error) throw new Error(payload.error);
      if (!payload?.html) throw new Error('Aucun HTML généré.');
      setForm((p) => ({ ...p, content: payload.html as string }));
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Erreur lors de la génération IA.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleTitleChange = (value: string) => {
    setForm((prev) => {
      // Le slug suit le titre tant que la page n'a pas encore été créée.
      const autoSlug = !editing && (!prev.slug || prev.slug === slugify(prev.title));
      return { ...prev, title: value, slug: autoSlug ? slugify(value) : prev.slug };
    });
  };

  const handleSave = async () => {
    setFormError('');
    const title = form.title.trim();
    const slug = slugify(form.slug || form.title);
    if (!title) {
      setFormError('Donne un titre à ta page.');
      return;
    }
    if (!slug) {
      setFormError('Le lien (slug) de la page est invalide.');
      return;
    }
    setSaving(true);
    try {
      const payload: MerchantCustomPageInput = { ...form, title, slug };
      if (editing) {
        await update(editing.id, payload);
      } else {
        await create(payload);
      }
      setShowEditor(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.';
      setFormError(
        /duplicate|unique/i.test(msg)
          ? 'Ce lien est déjà utilisé par une autre page. Choisis-en un autre.'
          : msg,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (page: MerchantCustomPage) => {
    if (!window.confirm(`Supprimer la page « ${page.title} » ?`)) return;
    try {
      await remove(page.id);
    } catch {
      /* silencieux */
    }
  };

  const handleCopy = async (page: MerchantCustomPage) => {
    const url = publicUrl(page.slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(page.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* silencieux */
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Pages personnalisées</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Créez des pages libres, publiez-les sur votre site ou partagez-les par un lien direct.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nouvelle page
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : pages.length === 0 ? (
        <div className="bg-background-50 border border-dashed border-background-300/60 rounded-lg p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-pages-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucune page personnalisée</p>
          <p className="text-xs text-foreground-500 mt-1 mb-4">
            Ex : « À propos », « Nos engagements », une page promotion, un lookbook…
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Créer une page
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pages.map((page) => (
            <div key={page.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate">{page.title}</h3>
                  <p className="text-xs text-foreground-400 font-mono truncate mt-0.5">/p/{page.slug}</p>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${page.published ? 'bg-accent-100 text-accent-700' : 'bg-background-200/70 text-foreground-500'}`}>
                  {page.published ? 'Publiée' : 'Brouillon'}
                </span>
              </div>

              <p className="text-xs text-foreground-500 mt-3 line-clamp-3 flex-1">
                {page.content.replace(/<[^>]*>/g, ' ').replace(/\{\{[^}]+\}\}/g, '…').trim() || 'Page vide'}
              </p>

              <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                <button
                  onClick={() => openEdit(page)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i>Modifier
                </button>
                {page.published && (
                  <>
                    <a
                      href={publicUrl(page.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                    >
                      <i className="ri-external-link-line"></i>Voir
                    </a>
                    <button
                      onClick={() => handleCopy(page)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={copiedId === page.id ? 'ri-check-line text-accent-600' : 'ri-link'}></i>
                      {copiedId === page.id ? 'Copié !' : 'Copier le lien'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(page)}
                  className="ml-auto w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Supprimer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={saving ? undefined : () => setShowEditor(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl bg-background-50 rounded-lg border border-background-200/70 flex flex-col" style={{ maxHeight: '92vh' }}>
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <h3 className="text-base font-semibold text-foreground-950">
                {editing ? 'Modifier la page' : 'Nouvelle page'}
              </h3>
              <button
                onClick={() => setShowEditor(false)}
                disabled={saving}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="overflow-y-auto p-5 flex-1 space-y-4">
              {/* Design -> page par IA */}
              <div className="rounded-lg border border-accent-200/60 bg-accent-50/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-magic-line text-accent-600"></i>
                  <span className="text-sm font-semibold text-foreground-800">Générer depuis un design (IA)</span>
                </div>
                <p className="text-xs text-foreground-500 mb-3">Téléversez une maquette, un export Photoshop/Figma ou un dessin à la main : l'IA le convertit en page HTML fidèle.</p>
                <input
                  ref={designInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleDesignFile(f); e.target.value = ''; }}
                />
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => designInputRef.current?.click()}
                    className="relative w-24 h-24 rounded-lg border-2 border-dashed border-background-300/60 bg-background-50 flex items-center justify-center overflow-hidden cursor-pointer flex-shrink-0"
                  >
                    {designPreview ? (
                      <img src={designPreview} alt="Design" className="absolute inset-0 w-full h-full object-cover" />
                    ) : designUploading ? (
                      <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
                    ) : (
                      <span className="flex flex-col items-center gap-1 text-foreground-400">
                        <i className="ri-image-add-line text-xl"></i>
                        <span className="text-[10px]">Design</span>
                      </span>
                    )}
                  </button>
                  <div className="flex-1 space-y-2">
                    <textarea
                      value={aiInstructions}
                      onChange={(e) => setAiInstructions(e.target.value)}
                      rows={2}
                      placeholder="Instructions (ex : garde le menu en haut, couleurs chaudes, section produits en grille…)"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateFromDesign}
                      disabled={aiGenerating || (!designUrl && !designPreview)}
                      className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      {aiGenerating ? (<><i className="ri-loader-4-line animate-spin"></i>Génération...</>) : (<><i className="ri-magic-line"></i>Générer la page par IA</>)}
                    </button>
                  </div>
                </div>
                {aiError && (
                  <p className="mt-2 text-xs text-red-600 flex items-start gap-1.5">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>{aiError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre de la page</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    maxLength={120}
                    placeholder="Ex : Nos engagements"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Lien (slug)</label>
                  <div className="flex items-center gap-1 px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg">
                    <span className="text-sm text-foreground-400 font-mono">/p/</span>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm((p) => ({ ...p, slug: slugify(e.target.value) }))}
                      maxLength={60}
                      placeholder="nos-engagements"
                      className="flex-1 bg-transparent text-sm text-foreground-900 font-mono focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-foreground-600">Contenu de la page (HTML)</label>
                  <span className="text-[10px] text-foreground-400">Balises du site autorisées</span>
                </div>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                  rows={10}
                  placeholder={'<section class="section">\n  <div class="container">\n    <h1>{{site_name}}</h1>\n    <p>Votre texte…</p>\n  </div>\n</section>'}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 font-mono focus:outline-none focus:border-primary-300 transition-colors resize-y"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {TAG_HINTS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, content: `${p.content}${p.content ? ' ' : ''}${tag}` }))}
                      className="px-2 py-0.5 rounded-full bg-background-100 text-[10px] font-mono text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, published: !p.published }))}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 cursor-pointer"
              >
                <span className="text-sm text-foreground-700 flex items-center gap-2">
                  <i className={form.published ? 'ri-global-line text-accent-600' : 'ri-eye-off-line text-foreground-400'}></i>
                  {form.published ? 'Publiée sur mon site' : 'Brouillon (non publiée)'}
                </span>
                <span className={`w-9 h-5 rounded-full transition-colors relative ${form.published ? 'bg-primary-500' : 'bg-background-300/60'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-all ${form.published ? 'left-[18px]' : 'left-0.5'}`}></span>
                </span>
              </button>

              <p className="text-[11px] text-foreground-400">
                Une page non publiée reste accessible via son lien direct pour la partager (aperçu).
              </p>

              {formError && (
                <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-xs text-red-700">{formError}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 h-14 border-t border-background-200/70 flex-shrink-0">
              <button
                onClick={() => setShowEditor(false)}
                disabled={saving}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                {editing ? 'Enregistrer' : 'Créer la page'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}